import { toDeviceId } from '@studio/models/device';
import type { Device } from '@studio/models/device';

import type { DeepPartial } from '@tryiris-ai/common.protojs';
import type { DeviceSetting as PBDeviceSetting } from '@tryiris-ai/common.protojs/agent/v1/device_settings/device_settings';

import type { T_Maybe } from '@tryiris-ai/js-types';
import { locateRoom } from '../../../utils.js';
import type { Proxy } from '#~/agent/index.js';
import { Slicer, useDepot } from '#~/depot/hooks.js';
import { isDeviceDeleted } from '#~/device/commands/teardown.js';
import type { State as DeviceSettingsState } from '#~/device/settings/index.js';
import { logger } from '#~/logger/index.js';

/** @internal */
export const useDeviceSettings = (
  deviceId: Device['id'],
  overrides?: Record<string, DeepPartial<PBDeviceSetting>>
): DeviceSettingsState => {
  const room = locateRoom(deviceId);

  const state =
    useDepot((µ) => {
      if (!room) {
        return undefined;
      }

      return new Slicer(µ).room(room.id).devices[deviceId]?.settings;
    }) ?? {};

  const nextState = { ...state };

  for (const [key, value] of Object.entries(overrides ?? {})) {
    if (value.toggle !== undefined || value.direct !== undefined || value.select !== undefined) {
      nextState[Number(key)] = value;
    }
  }

  return nextState;
};

/** @internal */
export const handleRestorePreset = async (
  agent: T_Maybe<Proxy>,
  deviceId: Device['id'],
  nextSettings: Record<string, DeepPartial<PBDeviceSetting>>
): Promise<void> => {
  // ENG-4477: a preset/defaults restore that resolves after the device was
  // deleted must not fire — an orphaned deviceSettings mutation at a
  // torn-down device only times out and races state sync.
  if (isDeviceDeleted(toDeviceId(deviceId))) {
    logger.warning(`Dropping settings restore for deleted device ${deviceId}.`);
    return;
  }

  const deviceSettings: Record<string, DeepPartial<PBDeviceSetting>> = {};

  Object.entries(nextSettings).forEach(([key, value]) => {
    const nextSetting: DeepPartial<PBDeviceSetting> = {};

    if (value.toggle !== undefined) {
      nextSetting.toggle = value.toggle;
    }

    if (value.direct !== undefined) {
      nextSetting.direct = value.direct;
    }

    if (value.select !== undefined) {
      nextSetting.select = value.select;
    }

    deviceSettings[Number(key)] = nextSetting;
  });

  await agent?.mutate(
    {
      control: {
        device: {
          [deviceId]: {
            deviceSettings
          }
        }
      }
    },
    {}
  );
};
